import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  ShoppingBag,
  Package,
  TrendingUp,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { TrendyolCredentials } from '../services/trendyolApiService';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';

interface TrendyolConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (credentials: TrendyolCredentials, connectionId: string) => void;
}

export function TrendyolConnectModal({
  isOpen,
  onClose,
  onSuccess,
}: TrendyolConnectModalProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const { user } = useAuth();
  const { checkDuplicateApiKey } = useSubscription();

  const [sellerId, setSellerId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'validating' | 'success'>('form');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!sellerId.trim() || !apiKey.trim() || !apiSecret.trim()) {
      setError(isEnglish ? 'All fields are required' : 'Tüm alanlar zorunludur');
      return;
    }

    // Basic validation for Seller ID (should be numeric)
    if (!/^\d+$/.test(sellerId.trim())) {
      setError(isEnglish ? 'Seller ID must be numeric' : 'Satıcı ID sayısal olmalıdır');
      return;
    }

    if (!user?.uid) {
      setError(isEnglish ? 'Please login first' : 'Lütfen önce giriş yapın');
      return;
    }

    setIsValidating(true);
    setStep('validating');

    try {
      // Check for duplicate API key (mükerrer hesap kontrolü)
      const duplicateCheck = await checkDuplicateApiKey(sellerId.trim(), 'trendyol');
      if (duplicateCheck.isDuplicate) {
        setStep('form');
        setError(duplicateCheck.message || (isEnglish
          ? 'Duplicate account detected! This API key is already used in another account. Please check your main account or contact support.'
          : 'Mükerrer hesap tespit edildi! Bu API anahtarı başka bir hesapta kullanılmaktadır. Lütfen ana hesabınızı kontrol edin veya destek ile iletişime geçin.'));
        setIsValidating(false);
        return;
      }

      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';

      // Save connection via Cloud Function (validates and saves to Firestore)
      const response = await fetch(`${FUNCTIONS_URL}/saveTrendyolConnection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          sellerId: sellerId.trim(),
          apiKey: apiKey.trim(),
          apiSecret: apiSecret.trim(),
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setStep('form');
        setError(result.error || (isEnglish ? 'Invalid credentials' : 'Geçersiz kimlik bilgileri'));
        setIsValidating(false);
        return;
      }

      // Success!
      const credentials: TrendyolCredentials = {
        sellerId: sellerId.trim(),
        apiKey: apiKey.trim(),
        apiSecret: apiSecret.trim(),
      };

      setStep('success');
      setTimeout(() => {
        onSuccess(credentials, result.connectionId);
        handleClose();
      }, 1500);
    } catch (err) {
      setStep('form');
      setError(
        err instanceof Error
          ? err.message
          : isEnglish
          ? 'Connection failed. Please try again.'
          : 'Bağlantı başarısız. Lütfen tekrar deneyin.'
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleClose = () => {
    setSellerId('');
    setApiKey('');
    setApiSecret('');
    setShowApiKey(false);
    setShowApiSecret(false);
    setError(null);
    setStep('form');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-surface rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-border-default">
        {/* Header with Trendyol Branding */}
        <div
          className="relative p-6 pb-8"
          style={{
            background: 'linear-gradient(135deg, #F27A1A 0%, #FF9F43 100%)',
          }}
        >
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <X size={18} />
          </button>

          <div className="flex items-center gap-4">
            {/* Trendyol Logo */}
            <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center shadow-lg">
              <svg viewBox="0 0 32 32" className="w-10 h-10">
                <path
                  fill="#F27A1A"
                  d="M16 2C8.268 2 2 8.268 2 16s6.268 14 14 14 14-6.268 14-14S23.732 2 16 2zm0 25.5c-6.351 0-11.5-5.149-11.5-11.5S9.649 4.5 16 4.5 27.5 9.649 27.5 16 22.351 27.5 16 27.5z"
                />
                <path
                  fill="#F27A1A"
                  d="M16 8c-4.411 0-8 3.589-8 8s3.589 8 8 8 8-3.589 8-8-3.589-8-8-8zm0 13c-2.761 0-5-2.239-5-5s2.239-5 5-5 5 2.239 5 5-2.239 5-5 5z"
                />
              </svg>
            </div>
            <div className="text-white">
              <h2 className="text-xl font-bold">Trendyol</h2>
              <p className="text-white/80 text-sm">
                {isEnglish ? 'Connect your seller account' : 'Satıcı hesabınızı bağlayın'}
              </p>
            </div>
          </div>

          {/* Features */}
          <div className="flex gap-4 mt-6">
            {[
              { icon: ShoppingBag, label: isEnglish ? 'Orders' : 'Siparişler' },
              { icon: Package, label: isEnglish ? 'Products' : 'Ürünler' },
              { icon: TrendingUp, label: isEnglish ? 'Analytics' : 'Analitik' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-white/90 text-sm">
                <Icon size={16} />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'validating' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[#F27A1A]/10 flex items-center justify-center mx-auto mb-4">
                <Loader2 size={32} className="text-[#F27A1A] animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {isEnglish ? 'Validating credentials...' : 'Kimlik bilgileri doğrulanıyor...'}
              </h3>
              <p className="text-sm text-text-secondary">
                {isEnglish
                  ? 'Connecting to Trendyol API'
                  : 'Trendyol API\'sine bağlanılıyor'}
              </p>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-green-500" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {isEnglish ? 'Successfully connected!' : 'Başarıyla bağlandı!'}
              </h3>
              <p className="text-sm text-text-secondary">
                {isEnglish
                  ? 'Your Trendyol account is now connected'
                  : 'Trendyol hesabınız artık bağlı'}
              </p>
            </div>
          )}

          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error Banner */}
              {error && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                  <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* Info Banner */}
              <div className="flex items-start gap-3 p-4 rounded-xl bg-[#F27A1A]/10 border border-[#F27A1A]/30">
                <Info size={20} className="text-[#F27A1A] flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-[#F27A1A] font-medium mb-1">
                    {isEnglish ? 'Where to find your API credentials?' : 'API bilgilerinizi nereden bulabilirsiniz?'}
                  </p>
                  <p className="text-text-secondary">
                    {isEnglish
                      ? 'Go to Trendyol Seller Center → Account Details → Integration Details'
                      : 'Trendyol Satıcı Paneli → Hesap Bilgilerim → Entegrasyon Bilgileri'}
                  </p>
                </div>
              </div>

              {/* Seller ID */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Seller ID (Satıcı ID)
                </label>
                <input
                  type="text"
                  value={sellerId}
                  onChange={(e) => setSellerId(e.target.value)}
                  placeholder="123456"
                  className="w-full px-4 py-3 rounded-xl bg-bg-elevated border border-border-default focus:border-[#F27A1A] focus:outline-none focus:ring-2 focus:ring-[#F27A1A]/20 text-text-primary placeholder:text-text-tertiary transition-all"
                />
              </div>

              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  API Key
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="••••••••••••••••"
                    className="w-full px-4 py-3 pr-12 rounded-xl bg-bg-elevated border border-border-default focus:border-[#F27A1A] focus:outline-none focus:ring-2 focus:ring-[#F27A1A]/20 text-text-primary placeholder:text-text-tertiary transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary transition-colors"
                  >
                    {showApiKey ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* API Secret */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  API Secret
                </label>
                <div className="relative">
                  <input
                    type={showApiSecret ? 'text' : 'password'}
                    value={apiSecret}
                    onChange={(e) => setApiSecret(e.target.value)}
                    placeholder="••••••••••••••••"
                    className="w-full px-4 py-3 pr-12 rounded-xl bg-bg-elevated border border-border-default focus:border-[#F27A1A] focus:outline-none focus:ring-2 focus:ring-[#F27A1A]/20 text-text-primary placeholder:text-text-tertiary transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiSecret(!showApiSecret)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary transition-colors"
                  >
                    {showApiSecret ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 rounded-xl bg-bg-elevated border border-border-default text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-all font-medium"
                >
                  {isEnglish ? 'Cancel' : 'İptal'}
                </button>
                <button
                  type="submit"
                  disabled={isValidating}
                  className={cn(
                    'flex-1 px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2',
                    'bg-[#F27A1A] text-white hover:bg-[#E06910]',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {isValidating ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {isEnglish ? 'Connecting...' : 'Bağlanıyor...'}
                    </>
                  ) : (
                    isEnglish ? 'Connect' : 'Bağlan'
                  )}
                </button>
              </div>

              {/* Help Link */}
              <div className="text-center pt-2">
                <a
                  href="https://developers.trendyol.com/en/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-text-tertiary hover:text-[#F27A1A] transition-colors"
                >
                  <ExternalLink size={14} />
                  {isEnglish ? 'API Documentation' : 'API Dokümantasyonu'}
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
